import { usePromptToolboxContext } from '@Context/prompt-toolbox/PromptToolboxContext';
import { Comments, LibraryPrompt } from '@Models/types';
import { Triangle } from '@phosphor-icons/react';
import { useEffect, useState } from 'react';
import { MoonLoader } from 'react-spinners';
import Upvote from './Upvote';
import { useUtilityContext } from '@Context/prompt-toolbox/UtilityContext';
import { useAuth } from '@Context/CognitoAuthProvider';
import Markdown from 'react-markdown';
import { Trash } from '@phosphor-icons/react';
import ReactiveIcon from '@Common/ReactiveIcon';
import colors from '@Common/assets/colors';
import ConfirmationModal from '@Common/ConfirmationModal';
import SuccessModal from '@Common/SuccessModal';
interface LibraryItemProps {
  id: string | undefined;
}

const LibraryItem = (props: LibraryItemProps) => {
  const { id } = props;
  const { fetchLibraryPrompt, postComment, deleteComment } =
    usePromptToolboxContext();
  const { loaders } = useUtilityContext();
  const [loading, setLoading] = useState(true);
  const [prompt, setPrompt] = useState<LibraryPrompt | null>(null);
  const [comment, setComment] = useState<string>('');
  const [deleteIndex, setDeleteIndex] = useState<number>(-1);
  const [openSuccess, setOpenSuccess] = useState<boolean>(false);
  const { user } = useAuth();

  useEffect(() => {
    const fetchPrompt = async () => {
      try {
        if (!id) return;
        const result = await fetchLibraryPrompt(id);
        setPrompt(result);
      } catch (error) {
        console.error('Error fetching prompt:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPrompt();

    return () => {
      setLoading(false);
    };
  }, [id, fetchLibraryPrompt]);

  if (loading) {
    return (
      <div
        data-testid="test-library-item"
        className="flex h-[35rem] w-full min-w-[40rem] flex-col rounded-b-lg border-2 border-primary-700 bg-neutral-100"
      >
        <span className="flex h-full w-full flex-col items-center justify-center gap-y-6 p-5">
          <MoonLoader size={150} color="#071D49" />
          <span className="animate-pulse text-xs font-semibold tracking-wide text-gray-600">
            Library Prompts Loading
          </span>
        </span>
      </div>
    );
  }
  if (!prompt) {
    return null;
  }

  const handlePostComment = async () => {
    if (comment.length <= 0 || loaders.commentLibrary) return;
    const commentData: Comments = {
      email: String(user?.profile?.email),
      comment: comment,
    };
    await postComment(prompt.id, commentData);
    setComment('');
  };

  const handleDeleteComment = async () => {
    if (deleteIndex !== -1) {
      await deleteComment(prompt.id, deleteIndex);
    }
    setDeleteIndex(-1);
    setOpenSuccess(true);
  };

  return (
    <div
      key={`library-item-${id}`}
      className="flex h-[35rem] w-full min-w-[40rem] flex-col rounded-b-lg border-2 border-primary-700 bg-neutral-100"
    >
      <div className="flex h-0 w-full flex-1 flex-grow flex-row items-center gap-x-2 p-3">
        <Upvote votes={prompt.votes} promptId={prompt.id} />

        <div className="custom-scroll flex h-full w-full flex-col overflow-y-scroll border-2 bg-white p-2">
          {prompt.version.prompt}
        </div>
        <div className="flex h-full flex-col items-center gap-y-2">
          <div className="h-full rounded-full border-2 border-primary-700"></div>
          {/* ICONS */}
          <div className="flex flex-col gap-y-1">
            <Triangle weight="bold" className="rotate-90 text-primary-700" />
            <Triangle weight="bold" className="rotate-90 text-primary-700" />
          </div>
          <div className="h-full rounded-full border-2 border-primary-700"></div>
        </div>
        <div className="custom-scroll flex h-full w-full flex-col overflow-y-scroll border-2 bg-white p-2">
          <Markdown>{prompt.version.gpt_result}</Markdown>
        </div>
      </div>

      {/*COMMENT SECTION  */}
      <div className="flex flex-1 flex-col px-5">
        <div className="flex flex-col">
          <div className="h-1 w-full rounded-lg bg-primary-700">
            {/*divider*/}
          </div>
          <h2 className="primary-700 text-md m-2 font-semibold">Comments</h2>
          <div className="flex items-center">
            <textarea data-testid="lib-expand-comment"
              value={comment}
              onChange={(e) => setComment(e.target.value.trimStart())}
              onInput={(e) => {
                const target = e.target as HTMLTextAreaElement;
                target.style.height = 'auto'; // Reset height first
                target.style.height = `${Math.min(target.scrollHeight, 100)}px`; // Expand up to max height
              }}
              placeholder="Write comment here"
              className="custom-scroll mx-2 h-auto w-full resize-none overflow-y-auto rounded-lg border-2 px-2 pt-1 placeholder:italic focus:border-primary-700"
            ></textarea>
            <button data-testid="lib-post-button"
              onClick={handlePostComment}
              className="h-10 rounded-lg bg-primary-700 px-4 text-white"
            >
              Post
            </button>
          </div>

          <h2 className="mx-4 my-2 text-sm text-gray-500" data-testid="total-comments">
            {prompt.comments?.length ?? 0} Comments
          </h2>
        </div>

        {/* COMMENT CARDS */}
        <div className="custom-scroll flex h-0 flex-grow flex-col overflow-y-scroll">
          {prompt.comments?.map((comment, index) => (
            <div
              key={index}
              className="my-2 flex flex-col px-8 text-sm text-primary-700"
            >
              <h1 className="flex justify-between font-semibold">
                <span>{comment.email}</span>
                {prompt.author === user?.profile?.name ||
                comment.email === user?.profile?.email ? (
                  <div
                    className="ml-2 h-6 cursor-pointer rounded-md text-white"
                    onClick={() => setDeleteIndex(index)}
                  >
                    <ReactiveIcon
                      Icon={Trash}
                      color={colors.brandRed[500]}
                      clicked={false}
                    />
                  </div>
                ) : (
                  <div></div>
                )}
              </h1>
              <p className="font-normal">{comment.comment}</p>
            </div>
          ))}
        </div>
      </div>
      <ConfirmationModal
        title={'Delete Comment'}
        isOpen={deleteIndex !== -1}
        loading={loaders.deleteComment}
        confirmButtonStyle={{
          label: 'Delete',
          color: 'RED',
        }}
        message={
          <span>
            Do you wish to <b className="text-brandRed-300">DELETE </b>
            this comment?
          </span>
        }
        onConfirm={() => handleDeleteComment()}
        onCancel={() => setDeleteIndex(-1)}
      />
      <SuccessModal
        title={'Success'}
        message={`Comment Successfully Deleted!`}
        isOpen={openSuccess}
        confirmButtonStyle={{ label: 'OK' }}
        onConfirm={() => setOpenSuccess(false)}
      />
    </div>
  );
};

export default LibraryItem;
